import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// Metadata for custom bouquets from the builder
export interface CustomBouquetMetadata {
  isCustomBouquet: true;
  style: string | null;
  size: string | null;
  budget: string | null;
  totalStems: number;
  items: Array<{
    itemId: string;
    name: string;
    category: string;
    bundleCount: number;
    stems: number;
    pricePerBundle: number;
  }>;
  // Greeting message from builder (optional)
  greetingMessage?: string;
  // Customer info is now collected at checkout, not in builder
  customerInfo?: {
    name: string;
    phone: string;
    pickupDate: string;
    pickupTime: string;
    greetingMessage?: string;
  };
  createdAt: string;
}

export interface ShopProductDB {
  id: string;
  slug: string;
  name_he: string;
  category: string;
  price: number;
  compare_at_price: number | null;
  price_type: string;
  image_file: string | null;
  image_path: string;
  short_desc_he: string | null;
  in_stock: boolean;
  stock_quantity: number | null;
  tags: string[] | null;
  sort_order: number | null;
  enabled: boolean;
  is_age_restricted: boolean;
  created_at: string;
  updated_at: string;
}

// Transform DB format to match existing ShopProduct interface for compatibility
export interface ShopProduct {
  id: string;
  slug: string;
  nameHe: string;
  category: string;
  price: number;
  compareAtPrice?: number;
  priceType: 'fixed' | 'from';
  imageFile?: string;
  imagePath: string;
  shortDescHe?: string;
  inStock: boolean;
  tags?: string[];
  sortOrder?: number;
  isAgeRestricted?: boolean;
  // For custom bouquets from the builder
  customBouquetMetadata?: CustomBouquetMetadata;
}

function transformProduct(dbProduct: ShopProductDB): ShopProduct {
  return {
    id: dbProduct.id,
    slug: dbProduct.slug,
    nameHe: dbProduct.name_he,
    category: dbProduct.category,
    price: dbProduct.price,
    compareAtPrice: dbProduct.compare_at_price ?? undefined,
    priceType: dbProduct.price_type as 'fixed' | 'from',
    imageFile: dbProduct.image_file ?? undefined,
    imagePath: dbProduct.image_path,
    shortDescHe: dbProduct.short_desc_he ?? undefined,
    inStock: dbProduct.in_stock,
    tags: dbProduct.tags ?? undefined,
    sortOrder: dbProduct.sort_order ?? undefined,
    isAgeRestricted: dbProduct.is_age_restricted ?? false,
  };
}

export function useShopProducts() {
  return useQuery({
    queryKey: ['shop-products'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('shop_products')
        .select('*')
        .eq('enabled', true)
        .order('sort_order', { ascending: true });
      
      if (error) {
        console.error('Error fetching shop products:', error);
        throw error;
      }
      
      return (data as ShopProductDB[]).map(transformProduct);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useShopProductBySlug(slug: string) {
  return useQuery({
    queryKey: ['shop-product', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('shop_products')
        .select('*')
        .eq('slug', slug)
        .eq('enabled', true)
        .single();
      
      if (error) {
        console.error('Error fetching shop product:', error);
        throw error;
      }
      
      return transformProduct(data as ShopProductDB);
    },
    enabled: !!slug,
  });
}

export function useShopProductsByCategory(category: string) {
  return useQuery({
    queryKey: ['shop-products', 'category', category],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('shop_products')
        .select('*')
        .eq('category', category)
        .eq('enabled', true)
        .order('sort_order', { ascending: true });
      
      if (error) {
        console.error('Error fetching shop products by category:', error);
        throw error;
      }
      
      return (data as ShopProductDB[]).map(transformProduct);
    },
    enabled: !!category,
  });
}

// Categories definition with filterBy mode
export const SHOP_CATEGORIES = [
  { id: "bouquets", labelHe: "זרי פרחים", order: 1, filterBy: 'category' as const },
  { id: "sale-150", labelHe: "זרים עד 250 ₪", order: 2, filterBy: 'category' as const },
  { id: "arrangements", labelHe: "קופסאות ומארזים", order: 3, filterBy: 'category' as const },
  { id: "special", labelHe: "זרים מיוחדים", order: 4, filterBy: 'tag' as const },
  { id: "plants", labelHe: "עציצים & סחלבים", order: 5, filterBy: 'category' as const },
  { id: "bridal", labelHe: "זרי כלה", order: 6, filterBy: 'category' as const },
  { id: "addons", labelHe: "מוצרים משלימים", order: 7, filterBy: 'category' as const },
  { id: "winter", labelHe: "פרחי חורף", order: 8, filterBy: 'tag' as const },
] as const;

export type ShopCategory = typeof SHOP_CATEGORIES[number]['id'];
